import {
    AsOfBadge,
    Badge,
    Banner,
    DateTime,
    DetailRow,
    FieldGrid,
    ObjectRef,
    OrganizationIcon,
    PageHeader,
    PeopleIcon,
    PublicId,
    Section,
    StatusBadge,
    StoreIcon,
    Tree,
    type TreeNode,
} from '@bemor/web-kit/ui';
import { Link, useNavigate, useParams } from 'react-router-dom';

import type { StaffOrganization } from '@/api/types';
import { ActivityCard } from '@/components/activity';
import { Comments } from '@/components/comments';
import { DetailBody } from '@/components/detail';
import { detailTitle } from '@/components/detail-title';
import { LifecycleActions } from '@/lib/lifecycle';
import { P } from '@/lib/permissions';
import { useOne } from '@/lib/queries';

/**
 * BM-93 (design 0.9.0 `backoffice/organization`): the tree. Merchants expand to their stores and users; members and
 * invitations beside it; every node links to its page. Read-only but Suspend / Reactivate (decisions 1, 2, 9).
 */
export function OrganizationPage() {
    const { id = '' } = useParams();
    const navigate = useNavigate();
    const query = useOne<StaffOrganization>(`/staff/organizations/${id}`);
    const o = query.data?.data;
    return (
        <div>
            <PageHeader
                crumbs={[{ label: 'Organizations', href: '/organizations' }]}
                {...detailTitle(query, o?.name || 'Unnamed organization', id)}
                meta={
                    <>
                        <PublicId id={id} short={false} />
                        <AsOfBadge asOf={query.freshness.asOf} />
                        {o ? (
                            <StatusBadge
                                status={o.status}
                                domain="organization"
                            />
                        ) : null}
                    </>
                }
                secondaryAction={
                    o ? (
                        <LifecycleActions
                            invalidate={[`/staff/organizations/${id}`]}
                            actions={[
                                {
                                    key: 'suspend',
                                    path: `/staff/organizations/${id}/suspend`,
                                    label: 'Suspend',
                                    title: 'Suspend this organization',
                                    description:
                                        'Every approved merchant under it is suspended too. Reactivating the organization does not reactivate them.',
                                    permission: P.organizationsManage,
                                    destructive: true,
                                    enabled: o.status === 'active',
                                },
                                {
                                    key: 'reactivate',
                                    path: `/staff/organizations/${id}/reactivate`,
                                    label: 'Reactivate',
                                    title: 'Reactivate this organization',
                                    description:
                                        'The organization is active again; its merchants stay suspended until each is approved again.',
                                    permission: P.organizationsManage,
                                    enabled: o.status === 'suspended',
                                },
                            ]}
                        />
                    ) : null
                }
            />
            <DetailBody query={query} title="Organization">
                {(org) => {
                    const nodes: TreeNode[] = org.merchants.map((m, i) => ({
                        id: m.id,
                        label: (
                            <span className="inline-flex items-center gap-2">
                                <span>{m.display_name || 'No name yet'}</span>
                                <StatusBadge
                                    status={m.status}
                                    domain="merchant"
                                    size="sm"
                                />
                                <StatusBadge
                                    status={m.kyc_status}
                                    domain="kyc"
                                    size="sm"
                                />
                            </span>
                        ),
                        icon: <OrganizationIcon className="size-4" />,
                        trailing: (
                            <Link
                                to={`/merchants/${m.id}`}
                                className="text-xs underline"
                            >
                                Open
                            </Link>
                        ),
                        defaultOpen: i === 0,
                        testId: `node-${m.id}`,
                        children: [
                            {
                                id: `${m.id}-stores`,
                                label: 'Stores',
                                group: true,
                                children: m.stores.map((s) => ({
                                    id: s.id,
                                    label: (
                                        <span className="inline-flex items-center gap-2">
                                            <span>{s.display_name}</span>
                                            {s.is_default ? (
                                                <Badge
                                                    variant="neutral"
                                                    emphasis="outline"
                                                    size="sm"
                                                >
                                                    Default
                                                </Badge>
                                            ) : null}
                                            <StatusBadge
                                                status={s.status}
                                                domain="store"
                                                size="sm"
                                            />
                                        </span>
                                    ),
                                    icon: <StoreIcon className="size-4" />,
                                    onActivate: () =>
                                        navigate(`/stores/${s.id}`),
                                    testId: `node-${s.id}`,
                                })),
                            },
                            {
                                id: `${m.id}-users`,
                                label: 'Users',
                                group: true,
                                children: m.users.map((u) => ({
                                    id: `${m.id}-${u.id}`,
                                    label: (
                                        <span className="inline-flex items-center gap-2">
                                            <span>{u.name}</span>
                                            <span className="text-fg-2 text-xs">
                                                {u.email}
                                            </span>
                                            <Badge
                                                variant="neutral"
                                                emphasis="outline"
                                                size="sm"
                                            >
                                                {u.role}
                                                {u.implicit
                                                    ? ' (organization)'
                                                    : ''}
                                            </Badge>
                                        </span>
                                    ),
                                    icon: <PeopleIcon className="size-4" />,
                                    onActivate: () =>
                                        navigate(`/users/${u.id}`),
                                    testId: `node-${m.id}-${u.id}`,
                                })),
                            },
                        ],
                    }));
                    return (
                        <div className="space-y-4">
                            {org.status === 'suspended' ? (
                                <Banner
                                    tone="warning"
                                    title={`Suspended${org.suspended_at ? ' on ' : ''}`}
                                    data-testid="organization-suspended"
                                >
                                    {org.suspended_at ? (
                                        <DateTime value={org.suspended_at} />
                                    ) : null}
                                    {org.suspend_reason
                                        ? `: ${org.suspend_reason}`
                                        : null}
                                </Banner>
                            ) : null}
                            <Section
                                title="Organization"
                                testId="organization-head"
                            >
                                <FieldGrid>
                                    <DetailRow label="Owner" layout="stacked">
                                        {org.owner ? (
                                            <ObjectRef
                                                id={org.owner.id}
                                                label={`${org.owner.name} · ${org.owner.email}`}
                                                href={`/users/${org.owner.id}`}
                                            />
                                        ) : (
                                            '—'
                                        )}
                                    </DetailRow>
                                    <DetailRow label="Since" layout="stacked">
                                        <DateTime
                                            zone="inline"
                                            value={org.created_at}
                                            format="date"
                                        />
                                    </DetailRow>
                                </FieldGrid>
                            </Section>
                            <Section
                                title="Merchants, stores and users"
                                flush
                                testId="organization-tree"
                            >
                                {nodes.length === 0 ? (
                                    <p className="text-fg-2 m-0 px-5 py-4 [font:var(--body-sm)]">
                                        No merchant yet.
                                    </p>
                                ) : (
                                    <div className="px-2 py-2">
                                        <Tree
                                            nodes={nodes}
                                            label="Organization"
                                        />
                                    </div>
                                )}
                            </Section>
                            <Section
                                title="Members"
                                flush
                                testId="organization-members"
                            >
                                <ul className="m-0 list-none p-0">
                                    {org.members.map((m) => (
                                        <li
                                            key={m.id}
                                            className="border-border-1 flex flex-wrap items-center justify-between gap-2 border-t px-5 py-3 first:border-t-0"
                                        >
                                            <span className="flex min-w-0 flex-col">
                                                <ObjectRef
                                                    id={String(
                                                        (
                                                            m.user as {
                                                                id?: string;
                                                            }
                                                        ).id ?? m.id,
                                                    )}
                                                    label={String(
                                                        (
                                                            m.user as {
                                                                name?: string;
                                                            }
                                                        ).name ?? '',
                                                    )}
                                                    href={`/users/${String((m.user as { id?: string }).id ?? '')}`}
                                                />
                                                <span className="text-fg-2 truncate text-xs">
                                                    {String(
                                                        (
                                                            m.user as {
                                                                email?: string;
                                                            }
                                                        ).email ?? '',
                                                    )}
                                                </span>
                                            </span>
                                            <span className="flex items-center gap-2">
                                                <Badge
                                                    variant="neutral"
                                                    emphasis="outline"
                                                    size="sm"
                                                >
                                                    {m.role}
                                                </Badge>
                                                <StatusBadge
                                                    status={m.status}
                                                    domain="member"
                                                    size="sm"
                                                />
                                            </span>
                                        </li>
                                    ))}
                                    {org.members.length === 0 ? (
                                        <li className="text-fg-2 px-5 py-4 [font:var(--body-sm)]">
                                            No members.
                                        </li>
                                    ) : null}
                                </ul>
                            </Section>
                            <Section
                                title="Pending invitations"
                                flush
                                testId="organization-invitations"
                            >
                                <ul className="m-0 list-none p-0">
                                    {org.invitations.map((i) => (
                                        <li
                                            key={i.id}
                                            className="border-border-1 flex flex-wrap items-center justify-between gap-2 border-t px-5 py-3 first:border-t-0"
                                        >
                                            <span className="flex min-w-0 flex-col">
                                                <span>{i.name}</span>
                                                <span className="text-fg-2 truncate text-xs">
                                                    {i.email}
                                                </span>
                                            </span>
                                            <span className="text-fg-2 flex items-center gap-2 text-xs">
                                                <Badge
                                                    variant="neutral"
                                                    emphasis="outline"
                                                    size="sm"
                                                >
                                                    {i.role}
                                                </Badge>
                                                sent{' '}
                                                <DateTime
                                                    value={i.created_at}
                                                    format="date"
                                                />
                                            </span>
                                        </li>
                                    ))}
                                    {org.invitations.length === 0 ? (
                                        <li className="text-fg-2 px-5 py-4 [font:var(--body-sm)]">
                                            None.
                                        </li>
                                    ) : null}
                                </ul>
                            </Section>
                        </div>
                    );
                }}
            </DetailBody>
            <div className="mt-4">
                <Comments subjectType="organization" subjectId={id} />
            </div>
            <div className="mt-4">
                <ActivityCard entity="organization" id={id} />
            </div>
        </div>
    );
}
